import { initializeApp, getApps } from "firebase/app";
import {
  getAuth, createUserWithEmailAndPassword, signOut, onAuthStateChanged,
  getRedirectResult,
} from "firebase/auth";
import { getFirestore, doc, setDoc, updateDoc } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { firebaseConfig, conectarEmuladores } from "../firebase";
import { entrarComEmail, entrarComGoogle, configurarSessao, solicitarCodigoSenha, confirmarCodigoSenha, precisaConfirmarEmail, enviarCodigoConfirmacao, confirmarCodigoEmail } from "./printControlAuth";
import { validarPerfil } from "./perfilCliente";

/*
 * Autenticação do CLIENTE FINAL da loja, isolada do login do vendedor.
 *
 * Usa um app Firebase secundário ("cliente") com Auth E Firestore próprios.
 * - A sessão do cliente NÃO dispara o onAuthStateChanged do app admin (App.js).
 * - As operações do cliente usam `clienteDb` (autenticado como o cliente),
 *   o que permite endurecer as firestore.rules para reconhecer o cliente.
 *
 * Migração transparente: clientes antigos (senha em texto puro no Firestore)
 * são migrados para o Firebase Auth automaticamente no primeiro login, sem
 * perder ninguém. Após migrar, o doc recebe `authUid` e a senha é apagada.
 */

const APP_NAME = "cliente";
const clienteJaExistia = getApps().some(a => a.name === APP_NAME);
const clienteApp  = getApps().find(a => a.name === APP_NAME) || initializeApp(firebaseConfig, APP_NAME);
export const clienteAuth = getAuth(clienteApp);
export const clienteDb   = getFirestore(clienteApp);
if (!clienteJaExistia) conectarEmuladores(clienteApp, { auth: clienteAuth, db: clienteDb });

// Cloud Functions para migrar clientes legados e buscar conta por e-mail
// sem leitura pública de clientes_finais (ver firestore.rules: só permite
// `get` de um doc específico, nunca um `list`/query na coleção inteira).
const clienteFns        = getFunctions(clienteApp, "us-central1");
const cfLegadoLogin     = httpsCallable(clienteFns, "clienteLegadoLogin");
const cfLegadoRecuperar = httpsCallable(clienteFns, "clienteLegadoRecuperar");
const cfBuscarPorEmail  = httpsCallable(clienteFns, "clienteBuscarPorEmail");
const cfEnviarCelular   = httpsCallable(clienteFns, "enviarCodigoCelularCliente");
const cfConfirmarCelular = httpsCallable(clienteFns, "confirmarCodigoCelularCliente");

// Retorna { id, nome, celular, dataNasc, instagram } do doc existente, ou null — nunca
// um DocumentSnapshot (a busca roda no servidor, não é mais um getDocs local).
async function buscarDocCliente(vendedorId, email) {
  const r = await cfBuscarPorEmail({ vendedorId, email });
  return r.data?.existe ? r.data : null;
}

// Garante que o cliente tenha um doc nesta loja, vinculado ao authUid.
async function garantirDocLoja(vendedorId, user, extra = {}) {
  const existente = await buscarDocCliente(vendedorId, user.email);
  if (existente) {
    const patch = {};
    if (extra.nome)     patch.nome = extra.nome;
    if (extra.celular)  patch.celular = extra.celular;
    if (extra.dataNasc) patch.dataNasc = extra.dataNasc;
    // Um login não precisa regravar o perfil. Isso também evita negar a
    // entrada de clientes antigos cujo id de documento difere do Auth uid.
    if (Object.keys(patch).length) await updateDoc(doc(clienteDb, `users/${vendedorId}/clientes_finais/${existente.id}`), patch);
    return { uid: user.uid, cadastroId: existente.id, authUid: user.uid, email: user.email, nome: extra.nome || existente.nome, celular: extra.celular || existente.celular, dataNasc: extra.dataNasc || existente.dataNasc, instagram: existente.instagram || "" };
  }
  const id = user.uid;
  const novo = { email: user.email, authUid: user.uid, nome: extra.nome || "", celular: extra.celular || "", dataNasc: extra.dataNasc || "", data_criacao: new Date().toISOString() };
  await setDoc(doc(clienteDb, `users/${vendedorId}/clientes_finais/${id}`), novo);
  return { uid: id, authUid: user.uid, email: user.email, nome: novo.nome, celular: novo.celular, dataNasc: novo.dataNasc };
}

export async function cadastrarCliente(vendedorId, { email, senha, nome, celular, dataNasc, manterConectado = true }) {
  email = email.trim().toLowerCase();
  await configurarSessao(clienteAuth, manterConectado);
  let cred;
  try {
    cred = await createUserWithEmailAndPassword(clienteAuth, email, senha);
  } catch (e) {
    if (e.code !== "auth/email-already-in-use") throw e;
    try {
      cred = await entrarComEmail(clienteAuth, email, senha);
    } catch {
      // Email já tinha conta, mas a senha digitada agora não é a mesma da
      // conta existente — sem isso, o erro cru do signIn (auth/invalid-credential)
      // vazava pro usuário em vez da mensagem amigável de "email já cadastrado".
      const jaCadastrado = new Error("Este e-mail já está cadastrado nesta loja. Faça login com sua senha, ou clique em \"Esqueci minha senha\" se não lembrar.");
      jaCadastrado.code = "auth/email-already-in-use";
      throw jaCadastrado;
    }
  }
  return garantirDocLoja(vendedorId, cred.user, { nome: nome?.trim(), celular, dataNasc });
}

const FALHA_LOGIN = ["auth/user-not-found", "auth/invalid-credential", "auth/invalid-login-credentials", "auth/wrong-password"];

export async function loginCliente(vendedorId, { email, senha, manterConectado = true }) {
  email = email.trim().toLowerCase();
  try {
    const cred = await entrarComEmail(clienteAuth, email, senha, manterConectado);
    return garantirDocLoja(vendedorId, cred.user);
  } catch (e) {
    if (!FALHA_LOGIN.includes(e.code)) throw e;
    // Cliente legado: validação e migração no servidor (sem leitura pública)
    try {
      await cfLegadoLogin({ vendedorId, email, senha });
    } catch (err) {
      const code = err?.code || "";
      if (code.includes("not-found") || code.includes("permission-denied")) {
        const invalid = new Error("Email ou senha incorretos.");
        invalid.code = "auth/invalid-credential";
        throw invalid;
      }
      throw new Error(err?.message || "Não foi possível entrar. Tente novamente.");
    }
    // Conta criada no Auth pelo servidor — agora loga normalmente
    const cred = await entrarComEmail(clienteAuth, email, senha, manterConectado);
    return garantirDocLoja(vendedorId, cred.user);
  }
}

// Se o email já tem doc nesta loja, entra direto. Senão, devolve
// { novo:true, user } — a tela pede o celular (Google não fornece) antes de
// criar o cadastro, via completarCadastroGoogle().
async function finalizarLoginGoogle(vendedorId, user) {
  const existente = await buscarDocCliente(vendedorId, (user.email || "").toLowerCase());
  if (existente) return { novo: false, cliente: await garantirDocLoja(vendedorId, user) };
  return { novo: true, user };
}

// Marca (na mesma aba) que ESSA loja disparou um redirecionamento pro Google
// agora — usado só pra saber, ao voltar, se deve oferecer completar cadastro
// automaticamente (e não confundir com uma sessão Google já existente de
// outra loja, já que clienteAuth é compartilhado entre todas as lojas).
const CHAVE_REDIRECT = "cliente_google_redirect_loja";
export function consumirRetornoGoogle(vendedorId) {
  try {
    if (sessionStorage.getItem(CHAVE_REDIRECT) !== vendedorId) return false;
    sessionStorage.removeItem(CHAVE_REDIRECT);
    return true;
  } catch { return false; }
}

// Login/cadastro por Google.
export async function loginComGoogle(vendedorId, manterConectado = true) {
  const cred = await entrarComGoogle(clienteAuth, manterConectado);
  return finalizarLoginGoogle(vendedorId, cred.user);
}

// Só pra capturar erro de configuração do próprio redirecionamento (ex.:
// domínio não autorizado) — não é a fonte de verdade de quem voltou logado,
// isso é o listener de auth. Chamar uma vez ao carregar a página da loja.
export async function erroDoRedirectGoogle() {
  try { await getRedirectResult(clienteAuth); return null; }
  catch (e) { return e; }
}

export async function completarCadastroGoogle(vendedorId, user, extra) {
  return garantirDocLoja(vendedorId, user, extra);
}

export async function recuperarSenhaCliente(vendedorId, email) {
  try {
    await solicitarCodigoSenha(clienteAuth, email);
  } catch (error) {
    if (error?.code !== "functions/not-found") throw error;
    // Compatibilidade com clientes anteriores ao Firebase Auth.
    try { await cfLegadoRecuperar({ vendedorId, email:email.trim().toLowerCase() }); }
    catch (legacyError) { if (legacyError?.code === "functions/not-found") return; throw legacyError; }
    await solicitarCodigoSenha(clienteAuth, email);
  }
}

export function confirmarSenhaCliente(dados) {
  return confirmarCodigoSenha(clienteAuth, dados);
}

// Confirmação do e-mail do cliente por código (o e-mail leva o nome da loja).
// Sem ela, a sessão não conta como "logado" na loja e o servidor recusa o pedido.
export const clientePrecisaConfirmar = (user = clienteAuth.currentUser) => precisaConfirmarEmail(user);
export const enviarCodigoConfirmacaoCliente = vendedorId => enviarCodigoConfirmacao(clienteAuth, { vendedorId });
export const confirmarEmailCliente = codigo => confirmarCodigoEmail(clienteAuth, codigo);
export const usuarioClienteAtual = () => clienteAuth.currentUser;

// Celular (WhatsApp) do cadastro nesta loja, confirmado por código. Confirmado,
// o servidor grava o número verificado no cadastro — é para ele que vai a
// mensagem de compra quando o pagamento é confirmado. Ainda sem tela: por
// enquanto o cadastro só guarda o celular, e a confirmação vem depois.
// enviar →{ enviado, telefone (mascarado), aguardarSegundos? } | { semCanal } | { confirmado }
export const enviarCodigoCelularCliente = (vendedorId, telefone) => cfEnviarCelular({ vendedorId, telefone }).then(r => r.data || {});
export const confirmarCelularCliente = (vendedorId, codigo) => cfConfirmarCelular({ vendedorId, codigo: String(codigo || "").trim() }).then(r => r.data || {});

export function logoutCliente() { return signOut(clienteAuth); }
export function observarClienteAuth(cb) { return onAuthStateChanged(clienteAuth, cb); }

// Reconstrói os dados do cliente pra ESTA loja a partir da sessão do Firebase
// Auth já persistida (browserLocalPersistence) — não depende de nenhum
// snapshot salvo à mão, então funciona igual seja qual for o link usado
// pra acessar a loja (slug ou uid).
export async function carregarClienteAtual(vendedorId) {
  const user = clienteAuth.currentUser;
  if (!user) return null;
  // Não cria doc aqui: isso é só restauração de sessão. Se ainda não existe
  // cadastro (ex.: Google no meio do fluxo de completar nome/celular), não
  // pode criar um doc em branco — isso faz o loginComGoogle achar que o
  // cliente já existe e pular a etapa de completar cadastro.
  const existente = await buscarDocCliente(vendedorId, (user.email || "").toLowerCase());
  if (!existente) return null;
  // Restaurar uma sessão é somente leitura. O vínculo/migração é feito no login.
  return { uid:user.uid, cadastroId:existente.id, authUid:user.uid, email:user.email, nome:existente.nome || "", celular:existente.celular || "", dataNasc:existente.dataNasc || "", instagram:existente.instagram || "" };
}

// Edição do perfil pelo próprio cliente (Minha conta). Nome, celular e data de
// nascimento são obrigatórios; Instagram é opcional. O e-mail não muda aqui: ele é a chave
// da conta no Firebase Auth. Devolve o perfil atualizado pra sessão.
export async function atualizarPerfilCliente(vendedorId, cliente, dados) {
  const { erros, valores } = validarPerfil(dados);
  const primeiroErro = erros.nome || erros.celular || erros.dataNasc || erros.instagram;
  if (primeiroErro) { const e = new Error(primeiroErro); e.code = "perfil/invalido"; throw e; }
  const patch = { nome: valores.nome, celular: valores.celular, dataNasc: valores.dataNasc };
  // Só regrava o Instagram se mudou: enquanto o backend antigo não devolve o
  // campo, um formulário em branco não pode apagar o valor já salvo.
  if (valores.instagram !== (cliente.instagram || "")) patch.instagram = valores.instagram;
  try {
    await updateDoc(doc(clienteDb, `users/${vendedorId}/clientes_finais/${cliente.cadastroId || cliente.uid}`), patch);
  } catch (e) {
    if (e?.code === "permission-denied") {
      const negado = new Error("Não foi possível alterar este cadastro por aqui (conta antiga). Fale com a loja para atualizar seus dados.");
      negado.code = "perfil/negado";
      throw negado;
    }
    throw e;
  }
  return { ...cliente, ...patch };
}
